import { useState, useEffect, useCallback, useRef } from "react";

// Types
interface ZoneData {
  temp: number;
  humidity: number;
  co2: number;
  occupancy: "available" | "lessAvailable" | "crowded";
  totalOccupancy?: number;
  maxCapacity?: number;
  functionalCapacity?: number;
  occupancyPercentage?: string;
}

interface FloorData {
  [zoneName: string]: ZoneData;
}

interface ZoneDataCollection {
  [floorName: string]: FloorData;
}

interface IAQSensor {
  id: string;
  co2?: number;
  temperature?: number;
  humidity?: number;
  location?: string;
}

interface OccupancyData {
  area: string;
  occupancy: number | string;
  timestamp: number;
}

interface CRData {
  [key: string]: {
    occupancy: number;
    timestamp: number;
  };
}

interface MPRData {
  [key: string]: {
    occupancy: number;
    timestamp: number;
  };
}

// Hook for fetching and managing zone data
const useZoneData = () => {
  const [zoneData, setZoneData] = useState<ZoneDataCollection>({});
  const [floorAverages, setFloorAverages] = useState<
    Record<string, { temp: number; humidity: number; co2: number }>
  >({});
  const [crData, setCRData] = useState<CRData>({
    "1F-CR1": { occupancy: 0, timestamp: 0 },
    "1F-CR2": { occupancy: 0, timestamp: 0 },
  });
  const [mprData, setMPRData] = useState<MPRData>({
    "3F-MPR-V01": { occupancy: 0, timestamp: 0 },
    "3F-MPR-V02": { occupancy: 0, timestamp: 0 },
  });
  const [cpr2Data, setCPR2Data] = useState({ occupancy: 0, timestamp: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // IAQ Data states
  const [iaqData, setIaqData] = useState<ZoneDataCollection>({});
  const [deviceLocations, setDeviceLocations] = useState<
    Record<string, string>
  >({});
  const deviceLocationsRef = useRef<Record<string, string>>({});

  // Map for floor IDs to match the API format
  const floorIdMap = {
    "1/F": "1F",
    "M/F": "MF",
    "2/F": "2F",
    "3/F": "3F",
  };

  // Floor and zone availability mapping
  const floorZoneMapping = {
    "1/F": ["Zone A", "Zone B", "Zone C"],
    "M/F": ["Zone A", "Zone C"],
    "2/F": ["Zone A", "Zone B", "Zone C"],
    "3/F": ["Floor", "Multi Purpose Room 2", "Multi Purpose Room 1"],
  };

  



// KEEP this useEffect for IAQ data but simplify it:
useEffect(() => {
  if (Object.keys(deviceLocationsRef.current).length > 0) {
    fetchIAQData();
    const interval = setInterval(fetchIAQData, 60000);
    return () => clearInterval(interval);
  }
}, [deviceLocations]); // Only depend on deviceLocations

  // Parse location to determine floor and zone for IAQ data
  const parseLocation = useCallback((location: string) => {
    if (!location) return { floor: null, zone: null };

    let floor = null;
    let zone = null;

    // Special case handling for specific sensors
    if (location === "1C-DS778" || location === "1C-E440") {
      return { floor: "1F", zone: "Zone C" };
    } else if (location === "2C-REF") {
      return { floor: "2F", zone: "Zone C" };
    } else if (location === "MC-Co-Learning Zone") {
      return { floor: "MF", zone: "Zone C" };
    } else if (
      ["3C-LT301", "3C-E305"].includes(location) ||
      location.startsWith("3C") ||
      location.startsWith("3F")
    ) {
      return { floor: "3F", zone: "Floor" };
    }

    // Regular parsing for other locations
    if (/^\d/.test(location)) {
      floor = location.charAt(0);

      if (location.length > 1) {
        const zoneChar = location.charAt(1).toUpperCase();
        if (zoneChar === "A" || zoneChar === "S") {
          zone = "A";
        } else if (
          zoneChar === "B" ||
          (zoneChar === "C" && floor !== "1" && floor !== "2")
        ) {
          zone = "B";
        } else if (
          zoneChar === "N" ||
          (zoneChar === "C" && (floor === "1" || floor === "2"))
        ) {
          zone = "C";
        }
      }
    } else if (location.toUpperCase().startsWith("M")) {
      floor = "M";

      if (location.length > 1) {
        const zoneChar = location.charAt(1).toUpperCase();
        if (zoneChar === "A" || zoneChar === "S") {
          zone = "A";
        } else if (
          zoneChar === "B" ||
          (zoneChar === "C" && !location.includes("Co-Learning"))
        ) {
          zone = "B";
        } else if (zoneChar === "N" || location.includes("Co-Learning")) {
          zone = "C";
        }
      }
    }

    if (floor === "3") {
      zone = "B";
    }

    return {
      floor: floor ? `${floor}F` : null,
      zone: zone ? `Zone ${zone}` : null,
    };
  }, []);

  // Map floor codes to display format
  const displayFloorMap = {
    "1F": "1/F",
    MF: "M/F",
    "2F": "2/F",
    "3F": "3/F",
  };

  // Calculate floor averages for temperature, humidity, and CO2
  const calculateFloorAverages = useCallback(
    (zoneDataInput: ZoneDataCollection) => {
      const averages: Record<
        string,
        { temp: number; humidity: number; co2: number }
      > = {};

      Object.keys(zoneDataInput).forEach((floor) => {
        const availableZones =
          floorZoneMapping[floor as keyof typeof floorZoneMapping] || [];
        const zones = Object.keys(zoneDataInput[floor]).filter((zone) =>
          availableZones.includes(zone)
        );

        if (zones.length === 0) {
          averages[floor] = { temp: 0, humidity: 0, co2: 0 }; 

          return;
        }

        const tempSum = zones.reduce(
          (sum, zone) => sum + zoneDataInput[floor][zone].temp,
          0
        );
        const humiditySum = zones.reduce(
          (sum, zone) => sum + zoneDataInput[floor][zone].humidity,
          0
        );
        const co2Sum = zones.reduce(
          (sum, zone) => sum + zoneDataInput[floor][zone].co2,
          0
        );

        averages[floor] = {
          temp: Math.round(tempSum / zones.length),
          humidity: Math.round(humiditySum / zones.length),
          co2: Math.round(co2Sum / zones.length),
        };
      });

      return averages;
    },
    []
  );

  // Process IAQ sensor data
  const processIAQSensorData = useCallback(
    (sensors: IAQSensor[]) => {
      if (!sensors || sensors.length === 0) return {};

      const floorZoneGroups: Record<string, Record<string, any>> = {};

      sensors.forEach((sensor) => {
        if (!sensor.location) return;

        let floor, zone;

        // Special handling for specific IAQ sensors
        if (sensor.id === "IAQ-L07" || sensor.id === "IAQ-L08") {
          floor = "1F";
          zone = "Zone C";
        } else if (sensor.id === "IAQ-L13") {
          floor = "2F";
          zone = "Zone C";
        } else if (sensor.id === "IAQ-L10") {
          floor = "MF";
          zone = "Zone C";
        } else if (
          ["IAQ-L14", "IAQ-L16", "IAQ-L18", "IAQ-P05"].includes(sensor.id)
        ) {
          floor = "3F";
          zone = "Floor";
        } else if (sensor.id === "IAQ-L15") {
          floor = "3F";
          zone = "Multi Purpose Room 2";
        } else if (sensor.id === "IAQ-L17") {
          floor = "3F";
          zone = "Multi Purpose Room 1";
        } else {
          const parsedLocation = parseLocation(sensor.location);
          floor = parsedLocation.floor;
          zone = parsedLocation.zone;
        }

        const archiveSensorIds = ["IAQ-L11", "IAQ-P03"];
          if (floor === "2F" && archiveSensorIds.includes(sensor.id)) {
            return;
          }

        if (!floor || !zone) return;

        if (!floorZoneGroups[floor]) {
          floorZoneGroups[floor] = {};
        }

        if (!floorZoneGroups[floor][zone]) {
          floorZoneGroups[floor][zone] = {
            co2: [],
            temp: [],
            humidity: [],
          };
        }

        if (sensor.co2 && !isNaN(sensor.co2)) {
          floorZoneGroups[floor][zone].co2.push(sensor.co2);
        }

        if (sensor.temperature && !isNaN(sensor.temperature)) {
          floorZoneGroups[floor][zone].temp.push(sensor.temperature);
        }

        if (sensor.humidity && !isNaN(sensor.humidity)) {
          floorZoneGroups[floor][zone].humidity.push(sensor.humidity);
        }
      });

      // Calculate averages
      const processedData: ZoneDataCollection = {};

      Object.entries(floorZoneGroups).forEach(([floor, zones]) => {
        const displayFloor = displayFloorMap[floor] || floor;

        if (!processedData[displayFloor]) {
          processedData[displayFloor] = {};
        }

        Object.entries(zones).forEach(([zone, data]) => {
          const avgCo2 =
            data.co2.length > 0
              ? Math.round(
                  data.co2.reduce((sum: number, val: number) => sum + val, 0) /
                    data.co2.length
                )
              : 0;

          const avgTemp =
            data.temp.length > 0
              ? Math.round(
                  (data.temp.reduce(
                    (sum: number, val: number) => sum + val,
                    0
                  ) /
                    data.temp.length) *
                    10
                ) / 10
              : 0;

          const avgHumidity =
            data.humidity.length > 0
              ? Math.round(
                  data.humidity.reduce(
                    (sum: number, val: number) => sum + val,
                    0
                  ) / data.humidity.length
                )
              : 0;

          processedData[displayFloor][zone] = {
            co2: avgCo2,
            temp: avgTemp,
            humidity: avgHumidity,
            occupancy: "available" as const,
          };
        });
      });

      return processedData;
    },
    [parseLocation]
  );

  // Fetch Computer Room occupancy data
  const fetchOccupancyData = useCallback(async () => {
    try {
      const response = await fetch(
        "https://optimusc.flowfuse.cloud/lingnan-library-occupancy"
      );
      const data: OccupancyData[] = await response.json();

      const processedCRData: CRData = {};
      const processedMPRData: MPRData = {};

      if (Array.isArray(data)) {
        data.forEach((item) => {
          if (item.area === "1F-CR1" || item.area === "1F-CR2") {
            processedCRData[item.area] = {
              occupancy:
                typeof item.occupancy === "number" ? item.occupancy : 0,
              timestamp: item.timestamp,
            };
          }

          if (item.area === "3F-MPR-V01" || item.area === "3F-MPR-V02") {
            processedMPRData[item.area] = {
              occupancy:
                typeof item.occupancy === "number" ? item.occupancy : 0,
              timestamp: item.timestamp,
            };
          }
        });

        setCRData((prevData) => {
          if (JSON.stringify(prevData) !== JSON.stringify(processedCRData)) {
            return { ...prevData, ...processedCRData };
          }
          return prevData;
        });

        setMPRData((prevData) => {
          if (JSON.stringify(prevData) !== JSON.stringify(processedMPRData)) {
            return { ...prevData, ...processedMPRData };
          }
          return prevData;
        });
      }
    } catch (error) {
      console.error("Error fetching occupancy data:", error);
    }
  }, []);

  // Fetch Computer Room 2 data from DOS endpoint
  const fetchCPR2Data = useCallback(async () => {
    try {
      const response = await fetch(
        "https://optimusc.flowfuse.cloud/lingnan-library-dos"
      );
      const data = await response.json();

      let occupiedCount = 0;
      if (Array.isArray(data)) {
        data.forEach((item: any) => {
          if (
            item.area &&
            item.area.startsWith("CPR2-") &&
            (item.occupancy === "occupied" || item.occupancy === "countdown")
          ) {
            occupiedCount++;
          }
        });
      }

      setCPR2Data({
        occupancy: occupiedCount,
        timestamp: Date.now(),
      });
    } catch (error) {
      console.error("Error fetching CPR2 data:", error);
    }
  }, []);

  // Fetch IAQ data
  const fetchIAQData = useCallback(async () => {
    const currentDeviceLocations = deviceLocationsRef.current;

    if (Object.keys(currentDeviceLocations).length === 0) return;

    try {
      const response = await fetch("https://optimusc.flowfuse.cloud/iaq");
      const result = await response.json();

      const dataArray = Object.entries(result).map(
        ([id, values]: [string, any]) => {
          return {
            id,
            ...values,
            location: currentDeviceLocations[id] || "Unknown Location",
          };
        }
      );

      const iaqSensors = dataArray.filter(
        (sensor: any) =>
          sensor.id.startsWith("IAQ-P") || sensor.id.startsWith("IAQ-L")
      );

      const processedIAQData = processIAQSensorData(iaqSensors);
      setIaqData(processedIAQData);
    } catch (error) {
      console.error("Error fetching IAQ data:", error);
    }
  }, [processIAQSensorData]);

  // Fetch device locations for IAQ data
const fetchDeviceLocations = useCallback(async () => {
  try {
    const response = await fetch(
      "https://lnudevices-dot-optimus-hk.df.r.appspot.com/devices"
    );
    const devices = await response.json();

    const locationMapping: Record<string, string> = {};
    devices.forEach((device: any) => {
      locationMapping[device.device_id] = device.location;
    });

    setDeviceLocations(locationMapping);
    deviceLocationsRef.current = locationMapping;
  } catch (error) {
    console.error("Error fetching device locations:", error);
    setError("Failed to fetch device locations");
  }
}, []);

useEffect(() => {
  fetchDeviceLocations();
}, [fetchDeviceLocations]);

  // Fetch zone occupancy data
const fetchZoneData = useCallback(async () => {
  try {
    // Fetch both APIs simultaneously
    const [zonesResponse, liveResponse] = await Promise.all([
      fetch("https://optimusc.flowfuse.cloud/all-zones"),
      fetch("https://floor-zone-new-dot-optimus-lnu.df.r.appspot.com/lnu-footfall/floor-zone/live2")
    ]);

    const zonesData = await zonesResponse.json();
    const liveData = await liveResponse.json();

    if (!Array.isArray(liveData)) {
      console.warn("liveData is not an array:", liveData);
    }

    // Create a capacity and threshold lookup from the live API
    const capacityLookup: Record<string, Record<string, any>> = {};
    liveData.forEach((zone: any) => {
      const floorMap: Record<string, string> = {
        "1F": "1/F",
        "2F": "2/F", 
        "3F": "3/F",
        "MF": "M/F"
      };
      
      const zoneMap: Record<string, string> = {
        "South-zone": "Zone A",
        "South-Zone": "Zone A",
        "Central-zone": "Zone B", 
        "Central-Zone": "Zone B",
        "North-zone": "Zone C",
        "North-Zone": "Zone C"
      };

      const displayFloor = floorMap[zone.floor_id];
      let displayZone = zoneMap[zone.zone_name];
      
      // Handle 3F special case
      if (zone.floor_id === "3F" && zone.zone_name === "Central-zone") {
        displayZone = "Floor";
      }
      
      if (displayFloor && displayZone) {
        if (!capacityLookup[displayFloor]) {
          capacityLookup[displayFloor] = {};
        }
        capacityLookup[displayFloor][displayZone] = {
          maxCapacity: zone.max_capacity,
          functionalCapacity: zone.functional_capacity,
          firstThreshold: zone.first_threshold,
          secondThreshold: zone.second_threshold
        };
      }
    });

    const processedData: ZoneDataCollection = {};
    
    // Zone mapping from all-zones API keys to display names
    const zoneMapping: Record<string, { floor: string; zone: string }> = {
      "1F-zone-a": { floor: "1/F", zone: "Zone A" },
      "1F-zone-b": { floor: "1/F", zone: "Zone B" },
      "1F-zone-c": { floor: "1/F", zone: "Zone C" },
      "2F-zone-a": { floor: "2/F", zone: "Zone A" },
      "2F-zone-b": { floor: "2/F", zone: "Zone B" },
      "2F-zone-c": { floor: "2/F", zone: "Zone C" },
      "3F": { floor: "3/F", zone: "Floor" },
      "MF-zone-a": { floor: "M/F", zone: "Zone A" },
      "MF-zone-c": { floor: "M/F", zone: "Zone C" }
    };
    // Process each zone from the all-zones API
    Object.keys(zoneMapping).forEach(apiKey => {
      const zoneInfo = zoneMapping[apiKey];
      const zoneData = zonesData[apiKey];
      
      if (zoneData && zoneData.totals) {
        const floor = zoneInfo.floor;
        const zone = zoneInfo.zone;
        
        // Initialize floor object if needed
        if (!processedData[floor]) {
          processedData[floor] = {};
        }
        
        // Use regular occupancy for 1F Zone B, adjustedOccupancy for all others
        const occupancyValue = (floor === "1/F" && zone === "Zone B") 
          ? (zoneData.totals.occupancy || 0)
          : (zoneData.totals.adjustedOccupancy || 0);
        
        // Get capacity and threshold data for this zone
        const capacityData = capacityLookup[floor]?.[zone];
        const functionalCapacity = capacityData?.functionalCapacity || 50; // fallback
        const firstThreshold = capacityData?.firstThreshold || 40;
        const secondThreshold = capacityData?.secondThreshold || 70;
        
        // Calculate percentage based on functional capacity
        const calculatedPercentage = functionalCapacity > 0
          ? (occupancyValue / functionalCapacity) * 100
          : 0;
        const percentageForStatus = calculatedPercentage < 0 ? 0 : calculatedPercentage;
        
        // Calculate status based on functional capacity percentage
        let status: "available" | "lessAvailable" | "crowded" = "available";
        if (percentageForStatus >= secondThreshold) {
          status = "crowded";
        } else if (percentageForStatus >= firstThreshold) {
          status = "lessAvailable";
        }

        processedData[floor][zone] = {
          occupancy: status,
          totalOccupancy: occupancyValue,
          maxCapacity: capacityData?.maxCapacity || 50,
          functionalCapacity: functionalCapacity,
          occupancyPercentage: `${Math.round(percentageForStatus)}%`,
          // Default IAQ values (will be updated with real data)
          co2: 0,
          temp: 0,
          humidity: 0,
        };
      }
    });

    // Initialize Multi Purpose Room zones with default values
    if (processedData["3/F"]) {
      if (!processedData["3/F"]["Multi Purpose Room 1"]) {
        processedData["3/F"]["Multi Purpose Room 1"] = {
          co2: 0,
          temp: 0,
          humidity: 0,
          occupancy: "available",
        };
      }
      if (!processedData["3/F"]["Multi Purpose Room 2"]) {
        processedData["3/F"]["Multi Purpose Room 2"] = {
          co2: 0,
          temp: 0,
          humidity: 0,
          occupancy: "available",
        };
      }
    }

 // Combine with IAQ data if available
if (Object.keys(iaqData).length > 0) {
  Object.keys(processedData).forEach((floor) => {
    Object.keys(processedData[floor]).forEach((zone) => {
      if (iaqData[floor] && iaqData[floor][zone]) {
        processedData[floor][zone] = {
          ...processedData[floor][zone],
          // CHANGED: Only update if IAQ value is not 0
          co2: iaqData[floor][zone].co2 !== 0 ? iaqData[floor][zone].co2 : processedData[floor][zone].co2,
          temp: iaqData[floor][zone].temp !== 0 ? iaqData[floor][zone].temp : processedData[floor][zone].temp,
          humidity: iaqData[floor][zone].humidity !== 0 ? iaqData[floor][zone].humidity : processedData[floor][zone].humidity,
        };
      }
    });
  });
}

    setZoneData((prevData) => {
      if (JSON.stringify(prevData) !== JSON.stringify(processedData)) {
        return processedData;
      }
      return prevData;
    });

    // Calculate and set floor averages
    const averages = calculateFloorAverages(processedData);
    setFloorAverages(averages);

    if (loading) {
      setLoading(false);
    }
  } catch (error) {
    console.error("Error fetching zone data:", error);
    setError("Failed to fetch zone data");
    if (loading) {
      setLoading(false);
    }
  }
}, [iaqData, loading, calculateFloorAverages]);

  // Set up intervals for data fetching
  useEffect(() => {
    // Fetch occupancy data
    fetchOccupancyData();
    const occupancyInterval = setInterval(fetchOccupancyData, 60000);

    // Fetch CPR2 data
    fetchCPR2Data();
    const cpr2Interval = setInterval(fetchCPR2Data, 70000);

    return () => {
      clearInterval(occupancyInterval);
      clearInterval(cpr2Interval);
    };
  }, [fetchOccupancyData, fetchCPR2Data]);

  // Set up IAQ data fetching
  useEffect(() => {
    if (Object.keys(deviceLocationsRef.current).length > 0) {
      fetchIAQData();
      const iaqInterval = setInterval(fetchIAQData, 90000);
      return () => clearInterval(iaqInterval);
    }
  }, [deviceLocations, fetchIAQData]);

  // Set up zone data fetching
  useEffect(() => {
    fetchZoneData();
    const zoneInterval = setInterval(fetchZoneData, 45000);
    return () => clearInterval(zoneInterval);
  }, [fetchZoneData]);

  return {
    zoneData,
    floorAverages,
    crData,
    mprData,
    cpr2Data,
    loading,
    error,
    floorZoneMapping,
    refreshData: () => {
      fetchZoneData();
      fetchOccupancyData();
      fetchCPR2Data();
      fetchIAQData();
    },
  };
};

export default useZoneData;
